import { ref } from 'vue'

export interface ToastOptions {
  title: string
  message?: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
}

interface ToastItem extends ToastOptions {
  id: number
}

const toasts = ref<ToastItem[]>([])
let toastId = 0

export function useToast() {
  const show = (options: ToastOptions) => {
    const id = toastId++
    const toast: ToastItem = {
      id,
      type: options.type || 'info',
      duration: options.duration ?? 3000,
      ...options
    }
    
    toasts.value.push(toast)

    // Auto remove after duration + animation time
    const duration = toast.duration ?? 3000
    if (duration > 0) {
      setTimeout(() => {
        remove(id)
      }, duration + 300) // 300ms for animation
    }

    return id
  }

  const remove = (id: number) => {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const success = (title: string, message?: string, duration?: number) => {
    return show({ title, message, type: 'success', duration })
  }

  const error = (title: string, message?: string, duration?: number) => {
    return show({ title, message, type: 'error', duration })
  }

  const warning = (title: string, message?: string, duration?: number) => {
    return show({ title, message, type: 'warning', duration })
  }

  const info = (title: string, message?: string, duration?: number) => {
    return show({ title, message, type: 'info', duration })
  }

  return {
    toasts,
    show,
    remove,
    success,
    error,
    warning,
    info
  }
}
